import { useState, useEffect, useMemo } from "react"
import { Loader2, CheckCircle2, ExternalLink, Copy } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Button,
  Input,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from "@f2e/ui/components/base"
import { Agent } from "@/types"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  useVerifySlackCredentialsApi,
  useGetSlackWebhookUrlApi,
  useGetPublishedAgentsApi,
  agentResponseToAgent,
} from "@/hooks/api"
import { isMaskedValue } from "../utils"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface SlackConnectionData {
  botToken: string
  signingSecret: string
  teamId: string
  teamName: string
  botUserId: string
  agentId: string
}

export interface SlackConnectionFormProps {
  channelId: string
  workspaceId: string
  organizationId: string
  initialData?: Partial<SlackConnectionData>
  onSubmit: (data: SlackConnectionData) => void
  onCancel?: () => void
  isLoading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

const createSlackFormSchema = (t: (key: string) => string) =>
  z.object({
    botToken: z
      .string()
      .min(1, {
        message: t("workspace.channels.slack.form.errors.botTokenRequired"),
      })
      .regex(/^xoxb-/, {
        message: t("workspace.channels.slack.form.errors.botTokenInvalid"),
      }),
    signingSecret: z.string().min(1, {
      message: t("workspace.channels.slack.form.errors.signingSecretRequired"),
    }),
    agentId: z.string().min(1, {
      message: t("workspace.channels.slack.form.errors.agentRequired"),
    }),
  })

type SlackFormData = z.infer<ReturnType<typeof createSlackFormSchema>>

type VerifyStatus = "idle" | "verifying" | "verified" | "failed"

const getSlackFormDefaults = (
  initialData?: Partial<SlackConnectionData>
): SlackFormData => ({
  botToken: isMaskedValue(initialData?.botToken)
    ? ""
    : initialData?.botToken || "",
  signingSecret: isMaskedValue(initialData?.signingSecret)
    ? ""
    : initialData?.signingSecret || "",
  agentId: initialData?.agentId || "",
})

const getInitialVerifiedData = (initialData?: Partial<SlackConnectionData>) => {
  if (!initialData?.teamId) {
    return null
  }
  if (
    !initialData.botToken ||
    !initialData.signingSecret ||
    isMaskedValue(initialData.botToken) ||
    isMaskedValue(initialData.signingSecret)
  ) {
    return null
  }

  return {
    teamId: initialData.teamId,
    teamName: initialData.teamName || "",
    botUserId: initialData.botUserId || "",
  }
}

export const SlackConnectionForm = ({
  channelId,
  workspaceId,
  organizationId,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  onDirtyChange,
}: SlackConnectionFormProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()
  const initialBotToken = initialData?.botToken
  const initialSigningSecret = initialData?.signingSecret
  const initialAgentId = initialData?.agentId
  const initialTeamId = initialData?.teamId
  const initialTeamName = initialData?.teamName
  const initialBotUserId = initialData?.botUserId
  const formDefaults = useMemo(
    () =>
      getSlackFormDefaults({
        botToken: initialBotToken,
        signingSecret: initialSigningSecret,
        agentId: initialAgentId,
      }),
    [initialAgentId, initialBotToken, initialSigningSecret]
  )
  const initialVerifiedData = useMemo(
    () =>
      getInitialVerifiedData({
        botToken: initialBotToken,
        signingSecret: initialSigningSecret,
        teamId: initialTeamId,
        teamName: initialTeamName,
        botUserId: initialBotUserId,
      }),
    [
      initialBotToken,
      initialBotUserId,
      initialSigningSecret,
      initialTeamId,
      initialTeamName,
    ]
  )
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>("idle")
  const [verifiedData, setVerifiedData] = useState<{
    teamId: string
    teamName: string
    botUserId: string
  } | null>(initialVerifiedData)
  const [webhookUrl, setWebhookUrl] = useState<string>("")
  const [agents, setAgents] = useState<Agent[]>([])

  const SlackFormSchema = createSlackFormSchema(t)
  const form = useForm<SlackFormData>({
    resolver: zodResolver(SlackFormSchema),
    defaultValues: formDefaults,
  })
  const { isDirty } = form.formState

  // APIs
  const {
    data: verifyRespData,
    status: verifyStatus_,
    fetchFn: verifyCredentials,
  } = useVerifySlackCredentialsApi()

  const { data: webhookRespData, fetchFn: getWebhookUrl } =
    useGetSlackWebhookUrlApi()

  const { data: agentsRespData, fetchFn: getAgents } =
    useGetPublishedAgentsApi()

  // Load agents and webhook URL on mount
  useEffect(() => {
    if (workspaceId && organizationId) {
      getAgents({
        searchParams: new URLSearchParams({
          wid: workspaceId,
          oid: organizationId,
        }),
      })
    }
    if (channelId) {
      getWebhookUrl({
        searchParams: new URLSearchParams({ channel_id: channelId }),
      })
    }
  }, [getAgents, getWebhookUrl, channelId, workspaceId, organizationId])

  useEffect(() => {
    form.reset(formDefaults)
    setVerifiedData(initialVerifiedData)
    setVerifyStatus("idle")
  }, [channelId, form, formDefaults, initialVerifiedData])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Handle agents response
  useEffect(() => {
    if (agentsRespData?.data) {
      // API already returns only published agents, transform to Agent type
      setAgents(agentsRespData.data.map(agentResponseToAgent))
    }
  }, [agentsRespData])

  // Handle webhook URL response
  useEffect(() => {
    if (webhookRespData?.data?.webhook_url) {
      setWebhookUrl(webhookRespData.data.webhook_url)
    }
  }, [webhookRespData])

  // Handle verify response
  useEffect(() => {
    if (verifyStatus_ === FetchStatus.Loading) {
      setVerifyStatus("verifying")
      return
    }

    if (verifyStatus_ === FetchStatus.Error) {
      setVerifyStatus("failed")
      return
    }

    if (verifyStatus_ === FetchStatus.Success && verifyRespData?.data) {
      setVerifyStatus("verified")
      setVerifiedData({
        teamId: verifyRespData.data.team_id,
        teamName: verifyRespData.data.team_name,
        botUserId: verifyRespData.data.bot_user_id,
      })
      successToast({
        title: t("workspace.channels.slack.form.verified.title"),
        description: t("workspace.channels.slack.form.verified.description", {
          teamName: verifyRespData.data.team_name,
        }),
      })
    }
  }, [verifyRespData, verifyStatus_, successToast, t])

  const handleVerify = async () => {
    const botToken = form.getValues("botToken")
    const signingSecret = form.getValues("signingSecret")

    if (!botToken || !signingSecret) {
      alertToast({
        title: t("workspace.channels.slack.form.errors.verifyTitle"),
        description: t(
          "workspace.channels.slack.form.errors.verifyDescription"
        ),
      })
      return
    }

    setVerifyStatus("verifying")
    setVerifiedData(null)

    try {
      await verifyCredentials({
        jsonBody: {
          bot_token: botToken,
          signing_secret: signingSecret,
        },
      })
    } catch (error) {
      setVerifyStatus("failed")
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.channels.slack.form.errors.verifyFailedTitle"),
          fallbackDescription: t(
            "workspace.channels.slack.form.errors.verifyFailedDescription"
          ),
        }),
      })
    }
  }

  const handleSubmit = (data: SlackFormData) => {
    if (!verifiedData) {
      alertToast({
        title: t("workspace.channels.slack.form.errors.notVerified"),
        description: t("workspace.channels.slack.form.errors.pleaseVerify"),
      })
      return
    }

    onSubmit({
      ...data,
      teamId: verifiedData.teamId,
      teamName: verifiedData.teamName,
      botUserId: verifiedData.botUserId,
    })
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    successToast({
      title: t("common.copied"),
      description: t("common.copiedToClipboard"),
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Setup Instructions */}
        <Alert>
          <AlertDescription>
            <div className="space-y-3">
              <p className="font-medium">
                {t("workspace.channels.slack.setup.title")}
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                {t("workspace.channels.slack.setup.instructions")}
              </pre>
              <p className="text-sm text-muted-foreground italic">
                {t("workspace.channels.slack.setup.notes")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("workspace.channels.slack.setup.emailScopeNote")}
              </p>
              <a
                href="https://api.slack.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {t("workspace.channels.slack.setup.createApp")}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </AlertDescription>
        </Alert>

        {/* Webhook URL (read-only) */}
        {webhookUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("workspace.channels.slack.form.webhookUrl")}
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="font-mono text-xs bg-muted"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(webhookUrl)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("workspace.channels.slack.form.webhookUrlDescription")}
            </p>
          </div>
        )}

        {/* Bot Token */}
        <FormField
          control={form.control}
          name="botToken"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.slack.form.botToken")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="xoxb-..."
                  className="font-mono"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    setVerifyStatus("idle")
                    setVerifiedData(null)
                  }}
                />
              </FormControl>
              <FormDescription>
                {t("workspace.channels.slack.form.botTokenDescription")}
                {isMaskedValue(initialBotToken) && (
                  <span className="block">
                    {t("workspace.channels.slack.form.maskedValueNotice")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Signing Secret */}
        <FormField
          control={form.control}
          name="signingSecret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.slack.form.signingSecret")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    "workspace.channels.slack.form.signingSecretPlaceholder"
                  )}
                  className="font-mono"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    setVerifyStatus("idle")
                    setVerifiedData(null)
                  }}
                />
              </FormControl>
              <FormDescription>
                {t("workspace.channels.slack.form.signingSecretDescription")}
                {isMaskedValue(initialSigningSecret) && (
                  <span className="block">
                    {t("workspace.channels.slack.form.maskedValueNotice")}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Verify Button */}
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleVerify}
            disabled={verifyStatus === "verifying"}
          >
            {verifyStatus === "verifying" && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {verifyStatus === "verified" && (
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
            )}
            {t("workspace.channels.slack.form.verifyButton")}
          </Button>

          {verifiedData && (
            <span className="text-sm text-green-600">
              {t("workspace.channels.slack.form.connectedTo", {
                teamName: verifiedData.teamName,
              })}
            </span>
          )}
        </div>

        {/* Agent Selection */}
        <FormField
          control={form.control}
          name="agentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("workspace.channels.slack.form.agent")}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "workspace.channels.slack.form.agentPlaceholder"
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {t("workspace.channels.slack.form.agentDescription")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Actions */}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={isLoading || !verifiedData}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default SlackConnectionForm
